import { FlexBox, LoadingButton } from '@airship-platform/jam-web/components';
import { TrashIcon } from '@airship-platform/jam-web/icons';
import { zodResolver } from '@hookform/resolvers/zod';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Controller, useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router';
import { z } from 'zod';
import { useAppointmentProposalOptions } from '../../../data-access/appointmentProposalOptions';
import { SectionWrapper } from '../../../patterns';

const LeasingPlanFormSchema = z.object({
  optionsLeasingPlan: z.record(
    z.string(),
    z
      .object({
        title: z.string(),
        amount: z.string(),
      })
      .refine(
        (val) => {
          const hasTitle = val.title.trim().length > 0;
          const hasAmount = val.amount.length > 0 && +val.amount > 0;
          return (hasTitle && hasAmount) || (!hasTitle && !hasAmount);
        },
        { error: 'Both title and amount are required' }
      )
  ),
});

type LeasingPlanFormInputs = z.infer<typeof LeasingPlanFormSchema>;

const sanitizeDollarInput = (value: string): string | null => {
  if (!/^\d*\.?\d*$/.test(value)) return null;
  return value.replace(/^0+(?=\d)/, '');
};

const LeasingPlanPage: React.FC = () => {
  const navigate = useNavigate();
  const { options, updateAppointmentProposalOption, isLoading } =
    useAppointmentProposalOptions();

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm<LeasingPlanFormInputs>({
    resolver: zodResolver(LeasingPlanFormSchema),
    defaultValues: {
      optionsLeasingPlan: Object.fromEntries(
        options.map((o) => [
          o.appointmentProposalOptionId,
          {
            title: o.leasingPlan?.title ?? '',
            amount: o.leasingPlan?.amount?.toString() ?? '',
          },
        ])
      ),
    },
  });

  const onSubmit = async (data: LeasingPlanFormInputs) => {
    const updates = options.map((option) => {
      const entry = data.optionsLeasingPlan[option.appointmentProposalOptionId];
      const amount = +(entry?.amount ?? 0);
      const title = entry?.title?.trim() ?? '';

      if (
        option.leasingPlan?.amount === amount &&
        option.leasingPlan?.title === title
      ) {
        return Promise.resolve();
      }

      return updateAppointmentProposalOption(
        option.appointmentId,
        option.appointmentProposalId,
        option.appointmentProposalOptionId,
        {
          leasingPlan:
            amount > 0 && title
              ? { title, amount, type: 'FIXED' as const }
              : { title: '', amount: 0, type: 'FIXED' as const },
        }
      );
    });

    await Promise.all(updates);
    navigate(-1);
  };

  return (
    <SectionWrapper
      maxWidth={'48rem'}
      width={'100%'}
      header="Leasing plan"
      subheader="Set a leasing plan per option to display a fixed monthly payment."
      actionsButtons={{
        left: {
          color: 'softGold',
          component: Link,
          to: '../financing',
          label: 'Cancel',
        },
        right: {
          component: LoadingButton,
          label: 'Save',
          loading: isLoading,
          disabled: isLoading,
          color: isDirty ? 'primary' : 'softGold',
          onClick: handleSubmit(onSubmit),
        },
      }}
    >
      <FlexBox flexDirection={'column'} gap={3} width={'100%'}>
        <Typography variant={'body2'} color={'text.secondary'}>
          A leasing plan displays a fixed monthly payment on the proposal for
          each option. Enter a plan title (e.g. "60-Month Lease") and the
          monthly dollar amount the customer will see.
        </Typography>
        {options.map((option) => (
          <FlexBox
            key={option.appointmentProposalOptionId}
            gap={2}
            alignItems={'center'}
            justifyContent={'space-between'}
          >
            <Typography variant={'subtitle2'} sx={{ minWidth: 80 }}>
              {option.name}
            </Typography>

            <Controller
              name={`optionsLeasingPlan.${option.appointmentProposalOptionId}`}
              control={control}
              render={({
                field: { value: rawValue, onChange },
                fieldState: { error },
              }) => {
                const value = rawValue ?? { title: '', amount: '' };
                return (
                  <FlexBox gap={1} flexDirection={'column'}>
                    <FlexBox gap={1} alignItems={'center'}>
                      <TextField
                        value={value.title}
                        onChange={(e) =>
                          onChange({ ...value, title: e.target.value })
                        }
                        placeholder="Plan title"
                        sx={{ width: 180 }}
                      />
                      <TextField
                        value={value.amount}
                        onChange={(e) => {
                          const sanitized = sanitizeDollarInput(e.target.value);
                          if (sanitized !== null)
                            onChange({ ...value, amount: sanitized });
                        }}
                        type="text"
                        placeholder="0.00"
                        slotProps={{
                          input: {
                            inputMode: 'decimal',
                            startAdornment: (
                              <InputAdornment position="start">
                                <Typography variant="body2" color="textPrimary">
                                  $
                                </Typography>
                              </InputAdornment>
                            ),
                          },
                        }}
                        sx={{ width: 140 }}
                      />
                      <IconButton
                        color="softGold"
                        disabled={!value.title && !value.amount}
                        onClick={() => onChange({ title: '', amount: '' })}
                      >
                        <TrashIcon />
                      </IconButton>
                    </FlexBox>
                    {error && (
                      <Typography variant="body3" color="error">
                        {error?.message}
                      </Typography>
                    )}
                  </FlexBox>
                );
              }}
            />
          </FlexBox>
        ))}
      </FlexBox>
    </SectionWrapper>
  );
};

export default LeasingPlanPage;
